import useWindowDimensions from '@hooks/useWindowDimensions';
import { Skeleton } from '@mui/material';
import React from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { LearningArticleCard } from '../LearningArticleCard/LearningArticleCard';
import styles from './index.module.scss';

export const LearningCarousel: React.FunctionComponent<LearningCarouselProps> = ({
  loading,
  data,
  carouselType,
  navigate,
  tagDetails,
}) => {
  const { isDesktop, isTablet } = useWindowDimensions();
  const carouselId = `learning-carousel-${carouselType}`;
  const partialVisibilityGutterForDesktop = data && data?.length <= 4 ? 0 : 20;
  const partialVisibilityGutterForMobile = data && data?.length <= 1 ? 0 : 30;
  const partialVisibilityGutterForTable = data && data?.length <= 2 ? 0 : 30;

  const responsive = {
    desktop: {
      breakpoint: {
        max: 10000,
        min: 1024,
      },
      items: 4,
      partialVisibilityGutter: partialVisibilityGutterForDesktop,
    },
    mobile: {
      breakpoint: {
        max: 464,
        min: 0,
      },
      items: 1,
      partialVisibilityGutter: partialVisibilityGutterForMobile,
    },
    tablet: {
      breakpoint: {
        max: 1024,
        min: 464,
      },
      items: 2,
      partialVisibilityGutter: partialVisibilityGutterForTable,
    },
  };

  return (
    <div
      className={`${styles.carouselContainer}${
        loading ? ` ${styles.carouselContainerLoading}` : ''
      }`}
      role="region"
      aria-labelledby={`${carouselId}-title`}
      aria-roledescription="carousel"
    >
      <Carousel
        additionalTransfrom={2}
        arrows
        autoPlaySpeed={3000}
        centerMode={false}
        className={styles['gd-carousel']}
        containerClass=""
        ssr
        dotListClass=""
        draggable
        focusOnSelect={false}
        infinite={false}
        itemClass={styles.carouselItem}
        keyBoardControl
        minimumTouchDrag={30}
        pauseOnHover
        renderArrowsWhenDisabled={false}
        renderButtonGroupOutside
        renderDotsOutside={false}
        responsive={responsive}
        rewind={false}
        rewindWithAnimation={false}
        rtl={false}
        shouldResetAutoplay
        showDots={false}
        sliderClass=""
        slidesToSlide={1}
        swipeable
        partialVisible
        aria-live="polite"
      >
        {loading
          ? Array.from({ length: isDesktop ? 5 : isTablet ? 2 : 1 }).map((_, index) => (
              <div
                key={index}
                aria-label="Loading article"
                role="list"
                className={styles.carouselItemWrapper}
              >
                <Skeleton
                  role="listitem"
                  variant="rounded"
                  width="100%"
                  height={275}
                  className={styles.learningItemLoading}
                  animation="wave"
                />
              </div>
            ))
          : data?.map((dt, index) => {
              const itemsVisible = isDesktop ? 4 : isTablet ? 2 : 1;
              const isVisible = index < itemsVisible;

              return (
                <div
                  key={index}
                  role="list"
                  className={styles.carouselItemWrapper}
                  aria-hidden={!isVisible}
                >
                  <div tabIndex={!isVisible ? -1 : undefined} role="listitem">
                    <LearningArticleCard
                      key={index}
                      data={dt}
                      navigate={navigate}
                      sectionType={carouselType}
                      tagDetails={{ tagId: dt?.tagId, title: dt?.tag }}
                      navigateTo="newPosts"
                      isHidden={!isVisible}
                    />
                  </div>
                </div>
              );
            })}
      </Carousel>
    </div>
  );
};

LearningCarousel.defaultProps = {
  data: [],
  loading: false,
  tagDetails: { tagId: -1, title: '' },
};

interface LearningCarouselProps {
  loading?: boolean;
  data?: {
    id: number;
    innerImageUrl: string;
    title: string;
    tagId?: number;
    tag?: string | undefined;
  }[];
  carouselType: string;
  navigate: (id: number, title: string) => void;
  tagDetails?: { tagId: number | undefined; title: string };
}
